import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { deliveryService, type ForceEndOutcome } from '@services/delivery.service';

interface ForceEndTripModalProps {
  open: boolean;
  onClose: () => void;
  /** Force-end by assignment (preferred), the order's active assignment, or the
   *  rider's active assignment — pass exactly one. */
  assignmentId?: string;
  orderId?: string;
  riderId?: string;
  /** Shown in the title, e.g. the rider's name or order number. */
  subject?: string;
  /** Called after a successful force-end (e.g. to refetch). */
  onDone?: () => void;
}

const OUTCOMES: { value: ForceEndOutcome; label: string; description: string }[] = [
  {
    value: 'delivered',
    label: 'Mark delivered',
    description:
      "The rider already delivered but the app won't finalise. Completes the trip and releases the rider's earning from escrow.",
  },
  {
    value: 'redispatch',
    label: 'Free rider & re-dispatch',
    description: 'The rider can’t deliver. Frees them and sends the order back out to find another rider.',
  },
  {
    value: 'cancel_refund',
    label: 'Cancel order & refund',
    description: 'The order can’t be fulfilled. Ends the trip, cancels the order and refunds the customer.',
  },
  {
    value: 'free_rider',
    label: 'Free rider only',
    description: 'Just detach the stuck rider and end the assignment. Handle the order manually afterwards.',
  },
];

export function ForceEndTripModal({
  open,
  onClose,
  assignmentId,
  orderId,
  riderId,
  subject,
  onDone,
}: ForceEndTripModalProps) {
  const [outcome, setOutcome] = useState<ForceEndOutcome>('delivered');
  const [reason, setReason] = useState('');
  const [confirmRefund, setConfirmRefund] = useState(false);

  const selectOutcome = (value: ForceEndOutcome) => {
    setOutcome(value);
    if (value !== 'cancel_refund') setConfirmRefund(false);
  };

  const needsApproval = outcome === 'cancel_refund';
  const canSubmit =
    !!reason.trim() && (!needsApproval || confirmRefund);

  const mutation = useMutation({
    mutationFn: () => {
      const r = reason.trim();
      if (assignmentId) return deliveryService.forceEndByAssignment(assignmentId, outcome, r);
      if (orderId) return deliveryService.forceEndByOrder(orderId, outcome, r);
      return deliveryService.forceEndByRider(riderId!, outcome, r);
    },
    onSuccess: () => {
      toast.success('Trip force-ended. The rider’s app has been updated.');
      setReason('');
      setConfirmRefund(false);
      onDone?.();
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not force-end the trip.';
      toast.error(message);
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[var(--state-warning)]" strokeWidth={1.5} />
            <h2 className="text-[18px] font-semibold text-[var(--text-primary)]">Force end trip</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        {subject && (
          <p className="text-[13px] text-[var(--text-muted)] mb-6">
            {subject}
          </p>
        )}

        <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-2">
          What happened?
        </label>
        <div className="space-y-2 mb-6">
          {OUTCOMES.map((o) => {
            const selected = outcome === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => selectOutcome(o.value)}
                className={`w-full text-left rounded-2xl border px-4 py-3 transition-colors ${
                  selected
                    ? 'border-[var(--color-gradient-from)] bg-[var(--bg-base)]'
                    : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                      selected
                        ? 'border-[var(--color-gradient-from)] bg-[var(--color-gradient-from)]'
                        : 'border-[var(--border-default)]'
                    }`}
                  />
                  <span className="text-[14px] font-semibold text-[var(--text-primary)]">{o.label}</span>
                </div>
                <p className="text-[12px] text-[var(--text-muted)] mt-1 ml-6">{o.description}</p>
              </button>
            );
          })}
        </div>

        <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
          Reason *
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Why is this trip being force-ended? (recorded on the trip)"
          className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] resize-none"
        />

        {/* Cancel + refund is destructive to the customer's order — require an
            explicit approval before it can be submitted. */}
        {needsApproval && (
          <label className="mt-4 flex items-start gap-3 rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmRefund}
              onChange={(e) => setConfirmRefund(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--state-danger)]"
            />
            <span className="text-[13px] text-[var(--state-danger)]">
              I approve cancelling this order and refunding the customer to their
              wallet. This can’t be undone.
            </span>
          </label>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !canSubmit}
            className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Ending…' : needsApproval ? 'Cancel order & refund' : 'Force end trip'}
          </button>
        </div>
      </div>
    </div>
  );
}
